import * as THREE from 'three';
import type { Brand } from '../brand/types';
import type { Tunables } from '../sim/tunables';
import { blobTexture } from './athletes';

/** The football at its real size (11 inches long, 6.7 across): a pointed ellipsoid with white laces. */
export class BallView {
  readonly mesh = new THREE.Group();
  /** Shadow on the grass, smaller and fainter the higher the ball is: how players read where it comes down. */
  readonly shadow: THREE.Mesh;
  private readonly spin = new THREE.Group();
  private readonly prev = new THREE.Vector3();
  private spinRate = 0;

  constructor(private readonly t: Tunables) {
    const b = t.ball;
    const geo = new THREE.SphereGeometry(b.radiusYards, b.widthSegments * 2, b.heightSegments * 2);
    const p = geo.attributes['position'] as THREE.BufferAttribute;
    // Prolate spheroid with pointed tips along z.
    for (let i = 0; i < p.count; i++) {
      const z = p.getZ(i) / b.radiusYards;
      const pinch = Math.sqrt(Math.max(0, 1 - z * z)) * 0.25 + 0.75;
      p.setXYZ(i, p.getX(i) * pinch, p.getY(i) * pinch, (z * b.lengthYards) / 2);
    }
    geo.computeVertexNormals();
    const ball = new THREE.Mesh(geo, new THREE.MeshPhongMaterial({ color: t.look.ball, shininess: 40 }));
    const laces = new THREE.Mesh(
      new THREE.BoxGeometry(b.radiusYards * 0.18, b.radiusYards * 0.12, b.lengthYards * 0.35),
      new THREE.MeshBasicMaterial({ color: t.look.ballLaces }),
    );
    laces.position.y = b.radiusYards * 0.96;
    this.spin.add(ball, laces);
    this.mesh.add(this.spin);
    this.mesh.scale.setScalar(b.displayScale);
    this.mesh.visible = false;
    this.shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ map: blobTexture(), transparent: true, depthWrite: false }),
    );
    this.shadow.renderOrder = -1;
    this.shadow.visible = false;
  }

  /** Places the ball; in flight it points along its path and spirals. */
  set(x: number, y: number, z: number, flying = false): void {
    this.prev.copy(this.mesh.position);
    this.mesh.position.set(x, y, z);
    const fx = this.t.fx;
    const k = Math.max(0, 1 - y / fx.ballShadowFadeYards);
    this.shadow.position.set(x, 0.025, z);
    this.shadow.scale.setScalar(fx.ballShadowYards * (0.45 + 0.55 * k));
    (this.shadow.material as THREE.MeshBasicMaterial).opacity = 0.25 + 0.6 * k;
    if (flying) {
      const dx = x - this.prev.x;
      const dy = y - this.prev.y;
      const dz = z - this.prev.z;
      if (dx * dx + dy * dy + dz * dz > 1e-6) this.mesh.lookAt(x + dx, y + dy, z + dz);
      this.spinRate = 30;
    } else {
      this.spinRate = 0;
      this.mesh.rotation.set(0.4, 0, 0.3);
    }
  }

  /**
   * In a player's hands: at the hand, turned with the body and tipped nose up, as a ball is carried high and
   * tight. The centre sits a little behind the hand so the front point is in the fingers.
   */
  hold(hand: THREE.Vector3, yaw: number): void {
    const b = this.t.ball;
    this.set(hand.x - Math.sin(yaw) * b.carryBackYards, hand.y, hand.z - Math.cos(yaw) * b.carryBackYards);
    this.mesh.rotation.set(-b.carryTiltRad, yaw, 0, 'YXZ');
    this.spin.rotation.z = 0;
  }

  update(dt: number): void {
    this.spin.rotation.z += this.spinRate * dt;
  }

  show(on: boolean): void {
    this.mesh.visible = on;
    this.shadow.visible = on;
  }
}

/**
 * Thick dotted throw arc from the QB to the lead point (GDD 6.4, reference 03): round brand-colour dots
 * that march toward the target. Shape follows power: low = lob, high = bullet.
 */
export class ArcPreview {
  readonly object: THREE.Points;
  private readonly pos: Float32Array;
  private time = 0;
  private readonly from = new THREE.Vector3();
  private readonly to = new THREE.Vector3();
  private peak = 0;

  constructor(
    brand: Brand,
    private readonly t: Tunables,
  ) {
    const n = t.fx.arcDots;
    this.pos = new Float32Array(n * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    const mat = new THREE.PointsMaterial({
      size: t.fx.arcDotYards,
      map: dotTexture(brand.colors.accent),
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    this.object = new THREE.Points(geo, mat);
    this.object.frustumCulled = false;
    this.object.renderOrder = 10;
    this.object.visible = false;
  }

  /** power 0 to 100. */
  set(from: THREE.Vector3, to: THREE.Vector3, power: number): void {
    const c = this.t.controls;
    const k = Math.min(1, Math.max(0, power / 100));
    this.from.copy(from);
    this.to.copy(to);
    this.peak = from.distanceTo(to) * (c.lobArcPerYard + (c.bulletArcPerYard - c.lobArcPerYard) * k);
    this.layout();
    this.object.visible = true;
  }

  hide(): void {
    this.object.visible = false;
  }

  update(dt: number): void {
    this.time += dt;
    if (this.object.visible) this.layout();
  }

  private layout(): void {
    const n = this.t.fx.arcDots;
    const shift = (this.time * this.t.fx.arcDotSpeed) % 1;
    const a = this.from;
    const b = this.to;
    for (let i = 0; i < n; i++) {
      const u = Math.min(1, (i + shift) / n);
      this.pos[i * 3] = a.x + (b.x - a.x) * u;
      this.pos[i * 3 + 1] = a.y + (b.y - a.y) * u + 4 * this.peak * u * (1 - u);
      this.pos[i * 3 + 2] = a.z + (b.z - a.z) * u;
    }
    (this.object.geometry.attributes['position'] as THREE.BufferAttribute).needsUpdate = true;
  }
}

/** A soft round dot in one colour, for point sprites. */
function dotTexture(color: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas not available');
  ctx.fillStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.arc(32, 32, 20, 0, Math.PI * 2);
  ctx.fill();
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Pulsing ring on the turf where the ball will land. */
export class TargetRing {
  readonly mesh: THREE.Mesh;
  private time = 0;

  constructor(brand: Brand) {
    this.mesh = new THREE.Mesh(
      new THREE.RingGeometry(0.75, 1, 32).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: brand.colors.accent, transparent: true, opacity: 0.9, depthWrite: false }),
    );
    this.mesh.visible = false;
  }

  show(x: number, z: number): void {
    this.mesh.position.set(x, 0.03, z);
    this.mesh.visible = true;
  }

  hide(): void {
    this.mesh.visible = false;
  }

  update(dt: number): void {
    this.time += dt;
    const s = 1 + 0.25 * Math.sin(this.time * 10);
    this.mesh.scale.set(s, 1, s);
  }
}

/** Tapered glowing ribbon behind the ball in flight, turned to face the camera. */
export class BallTrail {
  readonly mesh: THREE.Mesh;
  private readonly pts: Float32Array;
  private readonly verts: Float32Array;
  private count = 0;
  private readonly a = new THREE.Vector3();
  private readonly b = new THREE.Vector3();
  private readonly side = new THREE.Vector3();
  private readonly view = new THREE.Vector3();

  constructor(
    brand: Brand,
    private readonly n: number,
    private readonly width: number,
  ) {
    this.pts = new Float32Array(n * 3);
    this.verts = new Float32Array(n * 2 * 3);
    const col = new Float32Array(n * 2 * 3);
    const c = new THREE.Color(brand.colors.accent);
    for (let i = 0; i < n; i++) {
      const f = (1 - i / (n - 1)) ** 1.5;
      for (let k = 0; k < 2; k++) col.set([c.r * f, c.g * f, c.b * f], (i * 2 + k) * 3);
    }
    const index: number[] = [];
    for (let i = 0; i < n - 1; i++) {
      const v = i * 2;
      index.push(v, v + 1, v + 2, v + 1, v + 3, v + 2);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.verts, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geo.setIndex(index);
    this.mesh = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({ vertexColors: true, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, side: THREE.DoubleSide }),
    );
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  reset(): void {
    this.count = 0;
    this.mesh.visible = false;
  }

  push(p: THREE.Vector3): void {
    this.pts.copyWithin(3, 0, (this.n - 1) * 3);
    this.pts.set([p.x, p.y, p.z], 0);
    if (this.count === 0) for (let i = 1; i < this.n; i++) this.pts.set([p.x, p.y, p.z], i * 3);
    this.count = Math.min(this.n, this.count + 1);
    this.mesh.visible = true;
  }

  /** Rebuilds the ribbon so its flat side faces the camera. */
  update(camera: THREE.Camera): void {
    if (!this.mesh.visible) return;
    for (let i = 0; i < this.n; i++) {
      this.a.fromArray(this.pts, i * 3);
      this.b.fromArray(this.pts, Math.min(this.n - 1, i + 1) * 3);
      if (i === this.n - 1) this.b.fromArray(this.pts, (i - 1) * 3).sub(this.a).negate().add(this.a);
      this.view.copy(camera.position).sub(this.a);
      this.side.copy(this.b).sub(this.a).cross(this.view);
      const len = this.side.length();
      const half = (this.width / 2) * (1 - i / (this.n - 1));
      if (len > 1e-6) this.side.multiplyScalar(half / len);
      else this.side.set(0, 0, 0);
      this.verts.set([this.a.x + this.side.x, this.a.y + this.side.y, this.a.z + this.side.z], i * 6);
      this.verts.set([this.a.x - this.side.x, this.a.y - this.side.y, this.a.z - this.side.z], i * 6 + 3);
    }
    (this.mesh.geometry.attributes['position'] as THREE.BufferAttribute).needsUpdate = true;
  }
}

/** Touchdown confetti in the brand colours. */
export class Confetti {
  readonly mesh: THREE.InstancedMesh;
  private readonly p: Float32Array;
  private readonly v: Float32Array;
  private readonly r: Float32Array;
  private life = 0;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler();
  private readonly s = new THREE.Vector3(1, 1, 1);
  private readonly pos = new THREE.Vector3();

  constructor(
    brand: Brand,
    private readonly t: Tunables,
  ) {
    const n = t.fx.confettiCount;
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.18, 0.1), new THREE.MeshBasicMaterial({ side: THREE.DoubleSide }), n);
    this.mesh.frustumCulled = false;
    this.p = new Float32Array(n * 3);
    this.v = new Float32Array(n * 3);
    this.r = new Float32Array(n * 3);
    const colors = [brand.colors.accent, brand.colors.text, brand.colors.accent, brand.colors.primary];
    const c = new THREE.Color();
    for (let i = 0; i < n; i++) this.mesh.setColorAt(i, c.set(colors[i % colors.length]!));
    this.mesh.visible = false;
  }

  /** Visual only, so Math.random is fine here: it never touches an outcome. */
  burst(x: number, z: number): void {
    const n = this.t.fx.confettiCount;
    for (let i = 0; i < n; i++) {
      this.p[i * 3] = x + (Math.random() - 0.5) * 8;
      this.p[i * 3 + 1] = 6 + Math.random() * 6;
      this.p[i * 3 + 2] = z + (Math.random() - 0.5) * 8;
      this.v[i * 3] = (Math.random() - 0.5) * 6;
      this.v[i * 3 + 1] = Math.random() * 4;
      this.v[i * 3 + 2] = (Math.random() - 0.5) * 6;
      this.r[i * 3] = Math.random() * 6;
      this.r[i * 3 + 1] = Math.random() * 6;
      this.r[i * 3 + 2] = Math.random() * 6;
    }
    this.life = this.t.fx.confettiSeconds;
    this.mesh.visible = true;
  }

  update(dt: number): void {
    if (this.life <= 0) return;
    this.life -= dt;
    const n = this.t.fx.confettiCount;
    for (let i = 0; i < n; i++) {
      this.v[i * 3 + 1] = Math.max(-2.2, this.v[i * 3 + 1]! - 9 * dt);
      this.v[i * 3]! *= 0.98;
      this.v[i * 3 + 2]! *= 0.98;
      for (let k = 0; k < 3; k++) this.p[i * 3 + k]! += this.v[i * 3 + k]! * dt;
      this.e.set(this.r[i * 3]! * this.life * 3, this.r[i * 3 + 1]! * this.life * 3, this.r[i * 3 + 2]!);
      this.q.setFromEuler(this.e);
      this.pos.set(this.p[i * 3]!, Math.max(0.02, this.p[i * 3 + 1]!), this.p[i * 3 + 2]!);
      this.m.compose(this.pos, this.q, this.s);
      this.mesh.setMatrixAt(i, this.m);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.life <= 0) this.mesh.visible = false;
  }
}

/** A puff of turf and dust where a tackle lands. */
export class Puff {
  readonly points: THREE.Points;
  private readonly p: Float32Array;
  private readonly v: Float32Array;
  private readonly mat: THREE.PointsMaterial;
  private life = 0;

  constructor(private readonly t: Tunables) {
    const n = t.fx.puffCount;
    this.p = new Float32Array(n * 3);
    this.v = new Float32Array(n * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.p, 3));
    this.mat = new THREE.PointsMaterial({ map: dotTexture(t.look.turfPuff), size: t.fx.puffSizeYards * 1.6, transparent: true, depthWrite: false });
    this.points = new THREE.Points(geo, this.mat);
    this.points.frustumCulled = false;
    this.points.visible = false;
  }

  /** Visual only, so Math.random is fine: it never touches an outcome. */
  burst(x: number, z: number): void {
    const n = this.t.fx.puffCount;
    const sp = this.t.fx.puffSpeedYps;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = sp * (0.4 + Math.random() * 0.6);
      this.p[i * 3] = x + Math.cos(a) * 0.3;
      this.p[i * 3 + 1] = 0.1 + Math.random() * 0.2;
      this.p[i * 3 + 2] = z + Math.sin(a) * 0.3;
      this.v[i * 3] = Math.cos(a) * r;
      this.v[i * 3 + 1] = 1 + Math.random() * sp;
      this.v[i * 3 + 2] = Math.sin(a) * r;
    }
    this.life = this.t.fx.puffSeconds;
    this.mat.opacity = 1;
    this.points.visible = true;
  }

  update(dt: number): void {
    if (this.life <= 0) return;
    this.life -= dt;
    const n = this.t.fx.puffCount;
    for (let i = 0; i < n; i++) {
      this.v[i * 3 + 1]! -= 9 * dt;
      for (let k = 0; k < 3; k++) this.p[i * 3 + k]! += this.v[i * 3 + k]! * dt;
      this.p[i * 3 + 1] = Math.max(0.05, this.p[i * 3 + 1]!);
    }
    (this.points.geometry.attributes['position'] as THREE.BufferAttribute).needsUpdate = true;
    this.mat.opacity = Math.max(0, this.life / this.t.fx.puffSeconds);
    if (this.life <= 0) this.points.visible = false;
  }
}
